import { useState } from 'react';
import { EyeOff, Pencil, Printer, RotateCcw } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreatePaymentMutation, useHideSaleMutation, useIssueInvoiceMutation,
  usePaymentMethodsQuery, useRestoreSaleMutation, useSaleActionMutation, useSaleQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, ErrorState, Input, LoadingState, Modal, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SaleReceipt } from '@/components/SaleReceipt';
import { SaleEditModal } from './SaleEditModal';
import { PAYMENT_TONE, SALE_TONE } from './SalesPage';

/**
 * One sale, with the actions its status allows.
 *
 * Confirming is called out as the point of no return, because it is: stock
 * leaves, cost of goods is fixed, and a credit customer is charged.
 */
export function SaleDetailModal({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const format = useFormat();
  const { data: sale, isLoading, isError, error, refetch } = useSaleQuery(saleId);
  const { data: methods } = usePaymentMethodsQuery();

  const [runAction, actionState] = useSaleActionMutation();
  const [createPayment, paymentState] = useCreatePaymentMutation();
  const [issueInvoice, invoiceState] = useIssueInvoiceMutation();
  const [hideSale, hideState] = useHideSaleMutation();
  const [restoreSale, restoreState] = useRestoreSaleMutation();

  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingHide, setConfirmingHide] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState('');

  const act = async (action: string, body?: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    setNotice(null);
    try {
      await runAction({ id: saleId, action, body }).unwrap();
    } catch (actionError) {
      // Credit limits and stock shortfalls both come back with a usable message.
      setFailure(apiErrorMessage(actionError, `The sale could not be ${action}ed`));
    }
  };

  const takePayment = async (): Promise<void> => {
    setFailure(null);
    try {
      await createPayment({
        methodId,
        amount: Number(amount),
        customerId: sale?.customerId ?? undefined,
        allocations: [{ saleId, amount: Number(amount) }],
      }).unwrap();
      setPaying(false);
      setAmount('');
    } catch (paymentError) {
      setFailure(apiErrorMessage(paymentError, 'That payment could not be recorded'));
    }
  };

  const raiseInvoice = async (): Promise<void> => {
    setFailure(null);
    setNotice(null);
    try {
      const invoice = await issueInvoice({ saleId }).unwrap();
      setNotice(`Invoice ${invoice.number} issued.`);
    } catch (invoiceError) {
      setFailure(apiErrorMessage(invoiceError, 'That invoice could not be issued'));
    }
  };

  const isDraft = sale?.status === 'DRAFT' || sale?.status === 'PENDING';
  const isCompleted = sale?.status === 'COMPLETED';
  const owes = sale ? Number(sale.dueAmount) > 0 : false;
  const hidden = Boolean(sale?.deletedAt);

  const hide = async (): Promise<void> => {
    setFailure(null);
    try {
      await hideSale({ id: saleId, reason: hideReason || undefined }).unwrap();
      setConfirmingHide(false);
      setHideReason('');
      setNotice('Sale deleted and its goods returned to stock. Administrators can still open it.');
    } catch (hideError) {
      setFailure(apiErrorMessage(hideError, 'That sale could not be deleted'));
    }
  };

  const restore = async (): Promise<void> => {
    setFailure(null);
    try {
      await restoreSale(saleId).unwrap();
      setNotice('Back on the sales list, and its goods have left stock again.');
    } catch (restoreError) {
      setFailure(apiErrorMessage(restoreError, 'That sale could not be restored'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={sale ? `Sale ${sale.number}` : 'Sale'}
      description={sale ? (sale.customer?.name ?? 'Walk-in') : undefined}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>

          {sale && (
            <Button variant="outline" icon={Printer} onClick={() => setPrinting(true)}>
              Receipt
            </Button>
          )}

          {sale && !hidden && (
            <Can permission={P.SALE_CREATE}>
              <Button variant="outline" icon={Pencil} onClick={() => setEditing(true)}>
                Edit
              </Button>
            </Can>
          )}

          {sale && !hidden && (
            <Can permission={P.SALE_DELETE}>
              <Button
                variant="outline"
                icon={EyeOff}
                onClick={() => setConfirmingHide(true)}
              >
                Delete
              </Button>
            </Can>
          )}

          {sale && hidden && (
            <Can permission={P.SALE_DELETE}>
              <Button
                variant="outline"
                icon={RotateCcw}
                loading={restoreState.isLoading}
                onClick={() => void restore()}
              >
                Restore
              </Button>
            </Can>
          )}

          {isDraft && sale?.type !== 'QUOTATION' && (
            <>
              <Can permission={P.SALE_CANCEL}>
                <Button
                  variant="outline"
                  onClick={() => void act('cancel', { reason: 'Cancelled from the sale screen' })}
                >
                  Cancel sale
                </Button>
              </Can>
              <Can permission={P.SALE_CREATE}>
                <Button loading={actionState.isLoading} onClick={() => void act('confirm')}>
                  Confirm sale
                </Button>
              </Can>
            </>
          )}

          {isCompleted && owes && (
            <Can permission={P.PAYMENT_CREATE}>
              <Button
                onClick={() => {
                  setPaying(true);
                  setAmount(sale?.dueAmount ?? '');
                  setMethodId(methods?.find((m) => m.isDefault)?.id ?? methods?.[0]?.id ?? '');
                }}
              >
                Take payment
              </Button>
            </Can>
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : sale ? (
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}
          {notice && <Alert tone="success">{notice}</Alert>}

          {hidden && (
            <Alert tone="warning" title="This sale is deleted">
              It is off the sales list for everyone except members who may delete or audit
              sales. Its payments were voided, so the cash accounts no longer count them, and
              its goods were returned to the stock they came from.
              {sale.deleteReason && <span className="block mt-1">Reason: {sale.deleteReason}</span>}
            </Alert>
          )}

          {confirmingHide && (
            <Alert tone="danger" title="Delete this sale?">
              <p>
                Money taken against it is voided, so the cash account stops counting it.{' '}
                <strong>Its goods go back into stock</strong> where they came from, less
                anything already returned, and a credit customer no longer owes for it.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="min-w-56 flex-1">
                  <Input
                    label="Reason"
                    value={hideReason}
                    onChange={(event) => setHideReason(event.target.value)}
                    placeholder="Duplicate of SL-0042"
                  />
                </div>
                <Button
                  loading={hideState.isLoading}
                  onClick={() => void hide()}
                >
                  Delete sale
                </Button>
                <Button variant="outline" onClick={() => setConfirmingHide(false)}>
                  Keep it
                </Button>
              </div>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={SALE_TONE[sale.status]}>{sale.status.toLowerCase()}</Badge>
            {hidden && <Badge tone="danger">deleted</Badge>}
            <Badge tone={PAYMENT_TONE[sale.paymentStatus]}>
              {sale.paymentStatus.toLowerCase().replace(/_/g, ' ')}
            </Badge>
            <span className="text-xs text-muted">{sale.type.toLowerCase().replace(/_/g, ' ')}</span>
            <span className="text-xs text-muted">{formatDateTime(sale.issuedAt, format)}</span>
          </div>

          {isDraft && sale.type === 'QUOTATION' && (
            <Alert tone="info">
              A quotation is an offer, so it never moves stock. Raise an order or invoice when the
              customer accepts.
            </Alert>
          )}

          {isDraft && sale.type !== 'QUOTATION' && (
            <Alert tone="warning">
              Confirming releases the stock and fixes the cost of goods. A credit customer is
              charged, and the sale can only be undone with a return after that.
            </Alert>
          )}

          {paying && (
            <div className="space-y-3 rounded-lg border border-subtle bg-sunken p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Method"
                  value={methodId}
                  onChange={(event) => setMethodId(event.target.value)}
                >
                  {methods?.map((method) => (
                    <option key={method.id} value={method.id}>{method.name}</option>
                  ))}
                </Select>
                <Input
                  label="Amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  hint={`${formatMoney(sale.dueAmount, format)} outstanding`}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setPaying(false)}>Cancel</Button>
                <Button
                  size="sm"
                  variant="outline"
                  loading={paymentState.isLoading}
                  disabled={!methodId || !amount || Number(amount) <= 0}
                  onClick={() => void takePayment()}
                >
                  Record payment
                </Button>
              </div>
            </div>
          )}

          <Table>
            <thead>
              <tr>
                <Th>Item</Th>
                <Th numeric>Qty</Th>
                <Th numeric>Price</Th>
                <Th numeric>Discount</Th>
                <Th numeric>Tax</Th>
                <Th numeric>Total</Th>
              </tr>
            </thead>
            <tbody>
              {sale.items?.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <p>{item.description}</p>
                    {Number(item.returnedQty) > 0 && (
                      <p className="text-xs text-muted">
                        {formatQuantity(item.returnedQty, format)} returned
                      </p>
                    )}
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatQuantity(item.quantity, format)}
                  </Td>
                  <Td numeric className="text-secondary">{formatMoney(item.unitPrice, format)}</Td>
                  <Td numeric className="text-secondary">
                    {Number(item.discountAmount) > 0
                      ? formatMoney(item.discountAmount, format)
                      : '—'}
                  </Td>
                  <Td numeric className="text-secondary">
                    {Number(item.taxAmount) > 0 ? formatMoney(item.taxAmount, format) : '—'}
                  </Td>
                  <Td numeric>{formatMoney(item.lineTotal, format)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <Row label="Subtotal" value={formatMoney(sale.subtotal, format)} />
            {Number(sale.discountAmount) > 0 && (
              <Row label="Discount" value={`− ${formatMoney(sale.discountAmount, format)}`} />
            )}
            {Number(sale.taxAmount) > 0 && (
              <Row label="Tax" value={formatMoney(sale.taxAmount, format)} />
            )}
            <Row label="Total" value={formatMoney(sale.total, format)} strong />
            <Row label="Paid" value={formatMoney(sale.paidAmount, format)} />
            <Row label="Due" value={formatMoney(sale.dueAmount, format)} strong />
            {Number(sale.refundedAmount) > 0 && (
              <Row label="Refunded" value={formatMoney(sale.refundedAmount, format)} />
            )}
          </div>

          {sale.payments && sale.payments.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium">Payments</h3>
              <ul className="space-y-1 text-sm text-secondary">
                {sale.payments.map((allocation) => (
                  <li key={allocation.id}>
                    {allocation.payment.number} · {allocation.payment.method.name} ·{' '}
                    {formatMoney(allocation.amount, format)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isCompleted && (
            <Can permission={P.INVOICE_ISSUE}>
              <Button
                size="sm"
                variant="outline"
                loading={invoiceState.isLoading}
                onClick={() => void raiseInvoice()}
              >
                Issue invoice
              </Button>
            </Can>
          )}
        </div>
      ) : null}

      {printing && sale && <SaleReceipt sale={sale} onClose={() => setPrinting(false)} />}

      {editing && sale && (
        <SaleEditModal sale={sale} onClose={() => setEditing(false)} />
      )}
    </Modal>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted">{label}</span>
      <span className={strong ? 'font-semibold' : undefined}>{value}</span>
    </div>
  );
}
